import type { CatalogueType } from '@/config/types'
import type { PublicationCode } from '@/scopes/common/domain/types'
import type { FiltersInQuickSearch } from '@/scopes/search/quick-search/domain/types'
import { getPublicationCodeByCatalogueAndSiteName } from '@/scopes/common/domain/functions/publicationCodes'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import {
  getDefaultQuickSearchSelection,
} from '@/scopes/search/domain/policies/defaultFilters'

import { publicationCodeSelector } from '@/scopes/search/quick-search/components/selectors'
import { getCatalogues, getExtraCatalogueIcon } from '@/scopes/search/quick-search/domain/policies/sites'

import { useQuickSearchSelector } from '@/scopes/search/quick-search/infrastructure/store/useQuickSearchSelector'

import { Icon, tk, ToggleGroup, Typography } from '@sunwebgroup/ui-components'

import { useMemo } from 'react'

import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

const ToggleContainer = styled.div`
  width: calc(100% - 32px);
  display: flex;
  justify-content: center;
  position: absolute;
  top: 128px;
  left: 0;
  right: 0;
  margin: 0 auto;
  @media ${tk('mq_tablet')} {
    top: 157px;
    width: 100%;
  }
  @media ${tk('mq_desktop_md')} {
    top: 213px;
  }
`

const QuickSearchToggleGroup = styled(ToggleGroup)`
  background-color: #f3f0ed;
  padding: 2px;
  box-sizing: border-box;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px solid ${tk('theme_color_neutral_400')};
  max-width: 480px;
  width: 100%;

  > * {
    &:first-child {
      gap: unset;
      width: 100%;
    }
  }

  @media ${tk('mq_tablet')} {
    height: 48px;
    border-radius: 34px;
    max-width: unset;
    width: unset;
  }
`

const QuickSearchToggleItem = styled(ToggleGroup.Item)<{ width: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f3f0ed;
  border: none;
  border-radius: ${tk('radius_large')};
  min-height: 48px;
  min-width: 64px;
  padding: 6px ${tk('spacing_md')};
  ${({ width }) => `
      width: ${width};
    `}

  text-decoration: none;

  &[data-state='on'] {
    background-color: #4d453b;
  }

  &[data-state='on']:not(:disabled):hover {
    background-color: #352f28;
  }

  &[data-state='off']:not(:disabled):hover {
    background-color: #ded2c4;
  }

  > * {
    &:nth-child(2) {
      font-size: 12px;
    }
  }

  @media ${tk('mq_tablet')} {
    font-size: ${tk('font_size_md')};
    min-height: 40px;
    border-radius: 34px;
    flex-direction: row;
    padding: 9px ${tk('spacing_md')};
    width: unset;
    > * {
      &:nth-child(2) {
        font-size: ${tk('font_size_md')};
      }
    }
  }
`

const ToggleIcon = styled(Icon)`
  @media ${tk('mq_tablet')} {
    margin: 0 6px 0 0;
  }
`

function CatalogueButton({
  catalogue,
  onSwitch,
  customMobileWidthStyle,
}: {
  catalogue: CatalogueType
  onSwitch: (publicationCode: PublicationCode, selection: Partial<FiltersInQuickSearch>) => void
  customMobileWidthStyle?: string
}) {
  const intl = useIntl()
  const icon = catalogue === 'summer' ? 'IconSun' : 'IconMountainsSlope'
  const siteName = useConfiguration('siteName')
  const publicationCode = getPublicationCodeByCatalogueAndSiteName(catalogue, siteName)
  const selection = getDefaultQuickSearchSelection(publicationCode as PublicationCode)
  const clickHandler = () => {
    onSwitch(publicationCode, selection)
  }
  return (
    <QuickSearchToggleItem
      width={customMobileWidthStyle || '100%'}
      value={publicationCode}
      key={publicationCode}
      onClick={clickHandler}
    >
      <ToggleIcon glyph={icon} />
      <Typography>{intl.formatMessage({ id: `quicksearch_${catalogue}_catalogue_button` })}</Typography>
    </QuickSearchToggleItem>
  )
}

export default function QuickSearchToggle({
  onSwitch,
  isLoading,
}: {
  onSwitch: (publicationCode: PublicationCode, selection: Partial<FiltersInQuickSearch>) => void
  isLoading: boolean
}) {
  const intl = useIntl()
  const links = useConfiguration('links')
  const siteName = useConfiguration('siteName')
  const catalogues = getCatalogues(siteName)
  const extraCatalogueIcon = getExtraCatalogueIcon(siteName)
  const selectedPublicationCode = useQuickSearchSelector(publicationCodeSelector)

  const extraCatalogueClick = () => {
    window.open(links.extraCatalogue, '_blank')
  }

  const renderCatalogueItems = useMemo(() => {
    const itemsNo = extraCatalogueIcon ? catalogues.length + 1 : catalogues.length
    return (
      <>
        {catalogues.map((catalogue) => {
          return (
            <CatalogueButton
              customMobileWidthStyle={`calc(100% / ${itemsNo})`}
              catalogue={catalogue}
              key={catalogue}
              onSwitch={onSwitch}
            />
          )
        })}
        {extraCatalogueIcon && (
          <QuickSearchToggleItem width={`calc(100% / ${itemsNo})`} value="extra" onClick={extraCatalogueClick}>
            <ToggleIcon glyph={extraCatalogueIcon} />
            <Typography>{intl.formatMessage({ id: `quicksearch_extra_catalogue_button` })}</Typography>
          </QuickSearchToggleItem>
        )}
      </>
    )
  }, [catalogues, extraCatalogueIcon])

  return (
    <ToggleContainer>
      <QuickSearchToggleGroup disabled={isLoading} type="single" value={selectedPublicationCode}>
        {renderCatalogueItems}
      </QuickSearchToggleGroup>
    </ToggleContainer>
  )
}
